import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Input,
  Textarea,
  FormControl,
  Select,
  Option,
  Box,
  Typography,
  Alert,
  Chip
} from '@mui/joy';
import {
  Save,
  Cancel,
  Info,
  Warning,
  Error
} from '@mui/icons-material';
import { Note, CreateNoteData, UpdateNoteData, PRIORITY_DISPLAY_NAMES, VISIBILITY_DISPLAY_NAMES } from '../types/note';

interface NoteFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateNoteData | UpdateNoteData) => Promise<void>;
  note?: Note;
  selectedDate?: string;
  loading?: boolean;
}

const NoteForm: React.FC<NoteFormProps> = ({
  open,
  onClose,
  onSubmit,
  note,
  selectedDate,
  loading = false
}) => {
  const [formData, setFormData] = useState<CreateNoteData>({
    date: selectedDate || new Date().toISOString().split('T')[0],
    title: '',
    content: '',
    priority: 'normal',
    visibility: 'public'
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (note) {
      setFormData({
        date: note.date,
        title: note.title || '',
        content: note.content,
        priority: note.priority,
        visibility: note.visibility
      });
    } else if (selectedDate) {
      setFormData(prev => ({
        ...prev,
        date: selectedDate
      }));
    }
  }, [note, selectedDate]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.date) {
      newErrors.date = 'Date is required';
    }

    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    } else if (formData.content.length > 1000) {
      newErrors.content = 'Content must be 1000 characters or less';
    }

    if (formData.title && formData.title.length > 100) {
      newErrors.title = 'Title must be 100 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
      handleClose();
    } catch (error) {
      console.error('Error submitting note:', error);
    }
  };

  const handleClose = () => {
    setFormData({
      date: selectedDate || new Date().toISOString().split('T')[0],
      title: '',
      content: '',
      priority: 'normal',
      visibility: 'public'
    });
    setErrors({});
    onClose();
  };

  const handleInputChange = (field: keyof CreateNoteData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const getPriorityIcon = () => {
    switch (formData.priority) {
      case 'important':
        return <Warning />;
      case 'urgent':
        return <Error />;
      default:
        return <Info />;
    }
  };

  const characterCount = formData.content.length;
  const titleCharacterCount = formData.title?.length || 0;

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog size="md">
        <ModalClose />
        <DialogTitle>
          {note ? 'Edit Note' : 'Create Note'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Date Field */}
              <FormControl error={!!errors.date}>
                <Typography level="body-sm" sx={{ mb: 0.5 }}>Date</Typography>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                  required
                />
                {errors.date && (
                  <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                    {errors.date}
                  </Typography>
                )}
              </FormControl>

              {/* Title Field */}
              <FormControl error={!!errors.title}>
                <Typography level="body-sm" sx={{ mb: 0.5 }}>Title (optional)</Typography>
                <Input
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Enter a brief title for this note..."
                />
                <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                  {errors.title || `${titleCharacterCount}/100 characters`}
                </Typography>
              </FormControl>

              {/* Content Field */}
              <FormControl error={!!errors.content}>
                <Typography level="body-sm" sx={{ mb: 0.5 }}>Content</Typography>
                <Textarea
                  value={formData.content}
                  onChange={(e) => handleInputChange('content', e.target.value)}
                  placeholder="Enter your note content..."
                  minRows={4}
                  required
                />
                <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                  {errors.content || `${characterCount}/1000 characters`}
                </Typography>
              </FormControl>

              {/* Priority and Visibility Row */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl sx={{ flex: 1 }}>
                  <Typography level="body-sm" sx={{ mb: 0.5 }}>Priority</Typography>
                  <Select
                    value={formData.priority}
                    onChange={(_, value) => handleInputChange('priority', value || 'normal')}
                    startDecorator={getPriorityIcon()}
                  >
                    <Option value="normal">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Info />
                        {PRIORITY_DISPLAY_NAMES.normal}
                      </Box>
                    </Option>
                    <Option value="important">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Warning />
                        {PRIORITY_DISPLAY_NAMES.important}
                      </Box>
                    </Option>
                    <Option value="urgent">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Error />
                        {PRIORITY_DISPLAY_NAMES.urgent}
                      </Box>
                    </Option>
                  </Select>
                </FormControl>

                <FormControl sx={{ flex: 1 }}>
                  <Typography level="body-sm" sx={{ mb: 0.5 }}>Visibility</Typography>
                  <Select
                    value={formData.visibility}
                    onChange={(_, value) => handleInputChange('visibility', value || 'public')}
                  >
                    <Option value="public">{VISIBILITY_DISPLAY_NAMES.public}</Option>
                    <Option value="private">{VISIBILITY_DISPLAY_NAMES.private}</Option>
                  </Select>
                </FormControl>
              </Box>

              {/* Priority Preview */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography level="body-sm" color="neutral">
                  Preview:
                </Typography>
                <Chip
                  size="sm"
                  variant="soft"
                  startDecorator={getPriorityIcon()}
                  sx={{
                    backgroundColor: formData.priority === 'normal' ? '#3b82f6' : 
                                   formData.priority === 'important' ? '#f59e0b' : '#ef4444',
                    color: 'white'
                  }}
                >
                  {PRIORITY_DISPLAY_NAMES[formData.priority]}
                </Chip>
                <Chip
                  size="sm"
                  variant="outlined"
                >
                  {VISIBILITY_DISPLAY_NAMES[formData.visibility]}
                </Chip>
              </Box>

              {/* Character Count Warning */}
              {characterCount > 900 && (
                <Alert variant="soft" color="warning" size="sm">
                  You're approaching the 1000 character limit ({characterCount}/1000)
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={loading}
            >
              <Cancel sx={{ mr: 1 }} />
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
            >
              <Save sx={{ mr: 1 }} />
              {note ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </ModalDialog>
    </Modal>
  );
};

export default NoteForm; 